import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { MapPin, Loader2, Search, CheckCircle2 } from 'lucide-react';
import { Input } from '../ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AddressAutocomplete = ({ label, value, selected, onSelect, testId, placeholder, bias }) => {
  const [query, setQuery] = useState(value || '');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const skipNextSearch = useRef(true);
  const boxRef = useRef(null);

  useEffect(() => {
    if (value && value !== query) {
      skipNextSearch.current = true;
      setQuery(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    if (skipNextSearch.current) {
      skipNextSearch.current = false;
      return undefined;
    }
    if (query.trim().length < 3) {
      setResults([]);
      setSearched(false);
      return undefined;
    }
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const params = { q: query };
        if (bias?.latitude && bias?.longitude) {
          params.lat = bias.latitude;
          params.lon = bias.longitude;
        }
        const res = await axios.get(`${API}/address-search`, { params });
        setResults(res.data?.results || []);
        setOpen(true);
      } catch (err) {
        setResults([]);
      } finally {
        setLoading(false);
        setSearched(true);
      }
    }, 450);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  useEffect(() => {
    const close = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const choose = (item) => {
    skipNextSearch.current = true;
    setQuery(item.label);
    setOpen(false);
    onSelect(item);
  };

  return (
    <div ref={boxRef} className="relative">
      <label className="text-sm text-gray-600">{label}</label>
      <div className="relative mt-1">
        <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            if (selected) onSelect(null);
          }}
          onFocus={() => results.length > 0 && setOpen(true)}
          placeholder={placeholder}
          className="pl-9 pr-9"
          autoComplete="off"
          data-testid={`${testId}-input`}
        />
        {loading && <Loader2 className="w-4 h-4 text-tuggo-mint animate-spin absolute right-3 top-1/2 -translate-y-1/2" />}
        {!loading && selected && <CheckCircle2 className="w-4 h-4 text-green-600 absolute right-3 top-1/2 -translate-y-1/2" data-testid={`${testId}-selected-icon`} />}
      </div>
      {open && (
        <ul className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-64 overflow-auto" data-testid={`${testId}-results`}>
          {results.map((item, idx) => (
            <li key={`${item.label}-${idx}`}>
              <button
                type="button"
                onClick={() => choose(item)}
                className="w-full text-left px-3 py-2.5 hover:bg-tuggo-mint/10 flex items-start gap-2 transition-colors"
                data-testid={`${testId}-option-${idx}`}
              >
                <MapPin className="w-4 h-4 text-tuggo-mint mt-0.5 flex-shrink-0" />
                <span className="text-sm text-gray-800">{item.label}</span>
              </button>
            </li>
          ))}
          {searched && results.length === 0 && (
            <li className="px-3 py-2.5 text-sm text-gray-500" data-testid={`${testId}-no-results`}>
              Morada não encontrada. Indique o código postal abaixo.
            </li>
          )}
        </ul>
      )}
    </div>
  );
};
